import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { DeviceLabels, useMeetingManager } from 'amazon-chime-sdk-component-library-react'
import { MeetingSessionConfiguration } from 'amazon-chime-sdk-js'
import type { JoinOptions } from '@models/meeting-models'
import { useMeetingStore } from '@stores/meeting'
import { ROUTES } from '@constants/routes'
import { transformChimeData } from '@utils/chimeDataTransform'
import { useJoinMeetingMutation } from '@models/generated/graphql'

type JoinRequest = {
  options?: JoinOptions
}

export type JoinMeetingState = {
  join: (request?: JoinRequest) => Promise<void>
  isJoining: boolean
  error: Error | null
}

export function useJoinMeeting(appointmentId?: string): JoinMeetingState {
  const navigate = useNavigate()
  const setSession = useMeetingStore((state) => state.setSession)
  const meetingManager = useMeetingManager()
  const [joinMeeting] = useJoinMeetingMutation()

  const [isJoining, setIsJoining] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const join = useCallback(
    async ({ options }: JoinRequest = {}) => {
      setIsJoining(true)
      setError(null)

      try {
        if (!appointmentId) {
          throw new Error('AppointmentId manquant pour la connexion')
        }

        const preferences = options

        // First, call the joinMeeting mutation to ensure the meeting and attendee are created
        const { data: mutationData } = await joinMeeting({
          variables: { appointmentId }
        })

        const appointment = mutationData?.appointment

        if (!appointment) {
          throw new Error('Appointment not found')
        }

        const { chimeMeetingData, chimeAttendeeData } = appointment

        if (!chimeMeetingData || !chimeAttendeeData) {
          throw new Error('Chime meeting data not available')
        }

        // Transform backend snake_case data to PascalCase for Chime SDK
        const { meeting: transformedMeetingData, attendee: transformedAttendeeData } = transformChimeData(
          chimeMeetingData as Record<string, unknown>,
          chimeAttendeeData as Record<string, unknown>
        )

        // Create Chime meeting configuration from transformed data
        const nextConfiguration = new MeetingSessionConfiguration(transformedMeetingData, transformedAttendeeData)

        // Join the Chime meeting
        await meetingManager.join(nextConfiguration, {
          deviceLabels: DeviceLabels.AudioAndVideo
        })

        // Start the meeting session (connect to Chime)
        await meetingManager.start()

        // Wait for audioVideo to be available (polling with timeout)
        let attempts = 0
        const maxAttempts = 50 // 5 seconds max (50 * 100ms)
        while (!meetingManager.audioVideo && attempts < maxAttempts) {
          await new Promise((resolve) => setTimeout(resolve, 100))
          attempts++
        }

        if (!meetingManager.audioVideo) {
          throw new Error('AudioVideo non disponible après le démarrage de la réunion')
        }

        // Apply selected devices from waiting room
        if (preferences?.audioInputId) {
          try {
            await meetingManager.startAudioInputDevice(preferences?.audioInputId)
          } catch (err) {
            console.error('Erreur lors de la sélection du microphone:', err)
          }
        }

        if (preferences?.audioOutputId) {
          try {
            await meetingManager.startAudioOutputDevice(preferences?.audioOutputId)
          } catch (err) {
            console.error('Erreur lors de la sélection du haut-parleur:', err)
          }
        }

        if (preferences?.videoInputId) {
          try {
            await meetingManager.startVideoInputDevice(preferences?.videoInputId)
          } catch (err) {
            console.error('Erreur lors de la sélection de la caméra:', err)
          }
        }

        // Start local video if requested
        if (preferences?.startVideo) {
          try {
            if (!meetingManager.audioVideo.hasStartedLocalVideoTile()) {
              meetingManager.audioVideo.startLocalVideoTile()
            }
          } catch (err) {
            console.error('Erreur lors du démarrage de la vidéo locale:', err)
          }
        }

        // Start muted if requested (waiting room mic off)
        if (preferences?.startMuted) {
          try {
            meetingManager.audioVideo.realtimeMuteLocalAudio()
          } catch (err) {
            console.error('Erreur lors de la mise en sourdine du micro:', err)
          }
        }

        const extractedMeetingId = transformedMeetingData.MeetingId
        const extractedAttendeeId = transformedAttendeeData.AttendeeId

        if (!extractedMeetingId || !extractedAttendeeId) {
          throw new Error('Identifiants de réunion manquants.')
        }

        if (!meetingManager.meetingSession) {
          throw new Error("La session de réunion n'a pas pu être créée après la connexion.")
        }

        setSession({
          session: meetingManager.meetingSession,
          appointmentId: appointmentId,
          meetingId: extractedMeetingId,
          attendeeId: extractedAttendeeId,
          topic: null,
          startVideo: Boolean(preferences?.startVideo)
        })

        // Navigate to the meeting room page
        if (preferences?.mode === 'video') navigate(ROUTES.videoMeeting(appointmentId, extractedMeetingId))
        else navigate(ROUTES.phoneMeeting(appointmentId, extractedMeetingId))
      } catch (err) {
        const errorInstance = err instanceof Error ? err : new Error('Erreur lors de la connexion')
        setError(errorInstance)
        throw errorInstance
      } finally {
        setIsJoining(false)
      }
    },
    [meetingManager, setSession, navigate, appointmentId]
  )

  return {
    join,
    isJoining,
    error
  }
}
